'use client'

import { useEffect, useState } from 'react'
import { HIGH_CONTRAST_CLASS, HIGH_CONTRAST_KEY } from '@/lib/a11y/high-contrast'

/**
 * Toggle button for high-contrast mode.
 * Adds/removes the `.high-contrast` class on the <html> element
 * and persists the preference in localStorage.
 *
 * This component only reflects and changes the setting. Re-applying it on
 * every page load is the root layout's job (HIGH_CONTRAST_BOOT_SCRIPT) — this
 * toggle renders only on /settings, so it cannot be.
 */
export default function HighContrastToggle() {
  const [enabled, setEnabled] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    let stored: string | null = null
    try {
      stored = localStorage.getItem(HIGH_CONTRAST_KEY)
    } catch {
      // storage blocked — leave the setting off
    }
    if (stored === 'true') {
      setEnabled(true)
      document.documentElement.classList.add(HIGH_CONTRAST_CLASS)
    }
  }, [])

  function toggle() {
    const next = !enabled
    setEnabled(next)
    if (next) document.documentElement.classList.add(HIGH_CONTRAST_CLASS)
    else document.documentElement.classList.remove(HIGH_CONTRAST_CLASS)
    try {
      localStorage.setItem(HIGH_CONTRAST_KEY, next ? 'true' : 'false')
    } catch {
      // storage blocked — the mode still applies for this page, it just won't persist
    }
  }

  // Avoid hydration mismatch
  if (!mounted) {
    return (
      <button
        className="rounded-md p-2 hover:bg-muted transition-colors"
        aria-label="Toggle high contrast mode"
      >
        <span className="block h-4 w-4" />
      </button>
    )
  }

  return (
    <button
      onClick={toggle}
      className="rounded-md p-2 hover:bg-muted transition-colors"
      aria-label={enabled ? 'Disable high contrast mode' : 'Enable high contrast mode'}
      aria-pressed={enabled}
      title={enabled ? 'Disable high contrast mode' : 'Enable high contrast mode'}
    >
      {/* Eye icon with contrast indicator */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={enabled ? 3 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M12 5C5.636 5 2 12 2 12s3.636 7 10 7 10-7 10-7-3.636-7-10-7Z" />
        {enabled && <path d="M2 2l20 20" strokeWidth={2} />}
      </svg>
    </button>
  )
}
